import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Avatar,
  Chip,
  Button,
  Grid,
  Paper,
  Divider,
  Rating,
  Stack,
  IconButton,
  CircularProgress,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AccessTime as AccessTimeIcon,
  ThumbUp as ThumbUpIcon,
  Message as MessageIcon,
  Share as ShareIcon,
  Bookmark as BookmarkIcon,
  AttachMoney as AttachMoneyIcon,
  Chat as ChatIcon,
  Videocam as VideocamIcon,
  Computer as ComputerIcon,
  ShoppingCart as ShoppingCartIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import supabaseClient from '../supabaseClient';
import Nav1 from '../components/Nav1';
import Footer from '../components/Footer';

const PageWrapper = styled(Box)({
  backgroundColor: '#f8f9fa',
  minHeight: '100vh'
});

const ContentPaper = styled(Paper)({
  padding: '24px',
  marginBottom: '24px',
  borderRadius: '8px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
});

const HelpOptionCard = styled(Paper)(({ theme }) => ({
  padding: '24px',
  marginBottom: '16px',
  borderRadius: '8px',
  border: '1px solid #e0e0e0',
  transition: 'transform 0.2s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
  }
}));

const ProblemDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [problem, setProblem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedProblems, setRelatedProblems] = useState([]);
  const [experts, setExperts] = useState([]);

  useEffect(() => {
    fetchProblemDetails();
    fetchRelatedProblems();
    fetchExperts();
  }, [id]);

  const fetchProblemDetails = async () => {
    try {
      const { data, error } = await supabaseClient
        .from('problems')
        .select(`
          *,
          author:profiles(
            id,
            full_name,
            avatar_url
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      setProblem(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedProblems = async () => {
    try {
      const { data } = await supabaseClient
        .from('problems')
        .select('*')
        .limit(3);
      setRelatedProblems(data);
    } catch (err) {
      console.error('Error fetching related problems:', err);
    }
  };

  const fetchExperts = async () => {
    try {
      const { data } = await supabaseClient
        .from('experts')
        .select('*')
        .limit(3);
      setExperts(data);
    } catch (err) {
      console.error('Error fetching experts:', err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        p: 4 
      }}>
        <Typography variant="h6" color="error">Error: {error}</Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <PageWrapper>
      <Nav1 />
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ mb: 3 }}
            >
              Back to Problems
            </Button>

            <ContentPaper>
              {/* Problem Header */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h4" gutterBottom>
                  {problem.title}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar 
                    src={problem.author?.avatar_url}
                    alt={problem.author?.full_name}
                  />
                  <Box>
                    <Typography variant="subtitle1">
                      {problem.author?.full_name || 'Anonymous'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Posted on {new Date(problem.created_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Problem Details */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {problem.description}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Chip 
                    label={problem.category} 
                    color="primary" 
                    variant="outlined" 
                  />
                  <Chip 
                    label={`Budget: $${problem.budget}`}
                    icon={<AttachMoneyIcon />}
                    variant="outlined"
                  />
                  <Chip 
                    label={problem.status}
                    color={problem.status === 'open' ? 'success' : 'default'}
                    variant="outlined"
                  />
                </Stack>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                justifyContent: 'space-between',
                mt: 3 
              }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<MessageIcon />}
                >
                  Respond
                </Button>
                <Stack direction="row" spacing={1}>
                  <IconButton>
                    <ThumbUpIcon />
                  </IconButton>
                  <IconButton>
                    <ShareIcon />
                  </IconButton>
                  <IconButton>
                    <BookmarkIcon />
                  </IconButton>
                </Stack>
              </Box>
            </ContentPaper>

            {/* Expert Responses Section */}
            <ContentPaper>
              <Typography variant="h6" gutterBottom>
                Expert Responses
              </Typography>
              {/* Add expert responses here */}
            </ContentPaper>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            {/* Related Problems */}
            <ContentPaper>
              <Typography variant="h6" gutterBottom>
                Related Problems
              </Typography>
              <Stack spacing={2}>
                {relatedProblems.map((related) => (
                  <Box 
                    key={related.id}
                    sx={{ 
                      p: 2, 
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                        cursor: 'pointer'
                      }
                    }}
                    onClick={() => navigate(`/problem/${related.id}`)}
                  >
                    <Typography variant="subtitle2">
                      {related.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(related.created_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </ContentPaper>

            {/* Available Experts */}
            <ContentPaper>
              <Typography variant="h6" gutterBottom>
                Available Experts
              </Typography>
              <Stack spacing={2}>
                {experts.map((expert) => (
                  <Box 
                    key={expert.id}
                    sx={{ 
                      p: 2, 
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2
                    }}
                  >
                    <Avatar src={expert.avatar_url} />
                    <Box>
                      <Typography variant="subtitle2">
                        {expert.name}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {expert.specialty}
                      </Typography>
                      <Rating 
                        value={expert.rating} 
                        size="small" 
                        readOnly 
                      />
                    </Box>
                  </Box>
                ))}
              </Stack>
            </ContentPaper>

            {/* Help Options Section */}
            <ContentPaper>
              <Typography variant="h6" gutterBottom sx={{ color: '#108a00' }}>
                Help Options
              </Typography>
              
              {/* Live Chat Option */}
              <HelpOptionCard>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: '#e8f5e9' }}>
                    <ChatIcon sx={{ color: '#108a00' }} />
                  </Avatar>
                  <Box flexGrow={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Live Chat Consultation
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Chat with an expert now
                    </Typography>
                    <Typography variant="caption" color="success.main">
                      Starting at $30/30min
                    </Typography>
                  </Box>
                  <Button variant="contained" color="success">
                    Start Chat
                  </Button>
                </Stack>
              </HelpOptionCard>

              {/* Video Call Option */}
              <HelpOptionCard>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: '#e3f2fd' }}>
                    <VideocamIcon sx={{ color: '#1976d2' }} />
                  </Avatar>
                  <Box flexGrow={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Video Consultation
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Face-to-face expert guidance
                    </Typography>
                    <Typography variant="caption" color="primary.main">
                      Starting at $45/30min
                    </Typography>
                  </Box>
                  <Button variant="contained" color="primary">
                    Schedule
                  </Button>
                </Stack>
              </HelpOptionCard>

              {/* Remote Support Option */}
              <HelpOptionCard>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: '#fff3e0' }}>
                    <ComputerIcon sx={{ color: '#ed6c02' }} />
                  </Avatar>
                  <Box flexGrow={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Remote Support
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Let an expert fix it remotely
                    </Typography>
                    <Typography variant="caption" color="warning.main">
                      Starting at $60/hour
                    </Typography>
                  </Box>
                  <Button variant="contained" color="warning">
                    Connect
                  </Button>
                </Stack>
              </HelpOptionCard>

              {/* Product Suggestions */}
              <HelpOptionCard>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: '#f3e5f5' }}>
                    <ShoppingCartIcon sx={{ color: '#7b1fa2' }} />
                  </Avatar>
                  <Box flexGrow={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Product Solutions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Recommended products to fix this
                    </Typography>
                    <Stack direction="row" spacing={1} mt={1}>
                      {problem.category === 'SMARTPHONE' && (
                        <>
                          <Chip 
                            label="Screen Protector" 
                            size="small" 
                            variant="outlined"
                          />
                          <Chip 
                            label="Battery Pack" 
                            size="small" 
                            variant="outlined"
                          />
                        </>
                      )}
                      {problem.category === 'PC' && (
                        <>
                          <Chip 
                            label="RAM Upgrade" 
                            size="small" 
                            variant="outlined"
                          />
                          <Chip 
                            label="SSD Drive" 
                            size="small" 
                            variant="outlined"
                          />
                        </>
                      )}
                    </Stack>
                  </Box>
                  <Button variant="outlined" color="secondary">
                    View All
                  </Button>
                </Stack>
              </HelpOptionCard>

              {/* Custom Offer Section */}
              <HelpOptionCard>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Make a Custom Offer
                </Typography>
                <TextField
                  fullWidth
                  label="Your Rate ($/hour)"
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Message to Client"
                  multiline
                  rows={3}
                  sx={{ mb: 2 }}
                />
                <Button 
                  fullWidth 
                  variant="contained" 
                  color="primary"
                  startIcon={<SendIcon />}
                >
                  Send Offer
                </Button>
              </HelpOptionCard>
            </ContentPaper>
          </Grid>
        </Grid>
      </Container>
      <Footer />
    </PageWrapper>
  );
};

export default ProblemDetail; 